import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, FileText, Loader2, XCircle, CreditCard } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

export default function QuoteAcceptance() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [ship, setShip] = useState({ street: "", zip: "", city: "", phone: "" });

  useEffect(() => {
    api.get(`/public/quote/${token}`)
      .then((r) => { setQuote(r.data); setShip((s) => ({ ...s, phone: r.data.phone || "" })); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Angebot nicht gefunden"))
      .finally(() => setLoading(false));
  }, [token]);

  const setField = (k) => (e) => setShip((s) => ({ ...s, [k]: e.target.value }));

  const accept = async () => {
    if (!ship.street.trim() || !ship.zip.trim() || !ship.city.trim()) {
      toast.error("Bitte gib deine Lieferadresse ein.");
      return;
    }
    setAccepting(true);
    try {
      const { data } = await api.post(`/public/quote/${token}/accept`, ship);
      const { data: checkout } = await api.post("/payments/checkout", {
        order_id: data.order_id, origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Annehmen fehlgeschlagen.");
      setAccepting(false);
    }
  };

  const decline = async () => {
    setDeclining(true);
    try {
      await api.post(`/public/quote/${token}/decline`);
      setQuote((q) => ({ ...q, declined: true }));
      toast.success("Angebot abgelehnt.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ablehnen fehlgeschlagen.");
    } finally { setDeclining(false); }
  };

  return (
    <section data-testid="quote-acceptance" className="relative z-10 bg-[#0a0a0a] py-24 lg:py-36 border-t border-white/10 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#888] mb-4">Ihr Angebot</p>
        <h2 className="font-display text-white text-4xl sm:text-5xl leading-[0.95] mb-8">
          {loading ? "Lädt…" : error ? "Nicht verfügbar" : `Angebot ${quote.reference}`}
        </h2>

        {loading && (
          <div data-testid="quote-loading" className="flex items-center gap-2 text-[#888]"><Loader2 size={18} className="animate-spin" /> Angebot wird geladen…</div>
        )}

        {!loading && error && (
          <p data-testid="quote-error" className="text-red-400 text-sm">{error}</p>
        )}

        {!loading && quote && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="quote-details"
            className="rounded-3xl border border-white/10 bg-[#050505] p-8">
            <p className="text-white/80 text-sm">Hallo {quote.name}, hier ist unser Angebot zu Ihrer Anfrage „{quote.topic}":</p>

            <div className="mt-6 space-y-2">
              {quote.items.map((it, i) => (
                <div key={i} data-testid={`quote-item-${i}`} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                  <span className="text-white">{it.description} {it.qty > 1 ? `× ${it.qty}` : ""}</span>
                  <span className="font-mono text-[#ccc]">{((Number(it.qty) || 1) * (Number(it.unit_price) || 0)).toFixed(2)} €</span>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between rounded-2xl bg-white/5 px-5 py-4">
              <span className="text-white font-display text-xl">Gesamt</span>
              <span data-testid="quote-total" className="font-display text-2xl text-white">{quote.quote_total.toFixed(2)} €</span>
            </div>

            {quote.note && <p className="text-sm text-[#999] mt-5 leading-relaxed">{quote.note}</p>}

            {quote.quote_accepted ? (
              <div data-testid="quote-already-accepted" className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 flex items-center gap-3">
                <CheckCircle2 size={22} className="text-emerald-400 shrink-0" />
                <div>
                  <p className="text-emerald-300 font-medium">Angebot angenommen</p>
                  <p className="text-xs text-emerald-300/70 mt-0.5">Falls die Zahlung noch offen ist, lade die Seite neu und bezahle erneut.</p>
                </div>
              </div>
            ) : quote.declined ? (
              <div data-testid="quote-declined" className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/10 p-5 flex items-center gap-3">
                <XCircle size={22} className="text-red-400 shrink-0" />
                <p className="text-red-300 font-medium">Angebot abgelehnt. Vielen Dank für Ihre Rückmeldung.</p>
              </div>
            ) : (
              <>
                <div className="mt-8 space-y-3">
                  <label className="block text-xs font-mono uppercase tracking-widest text-[#888]">Lieferadresse für die Rücksendung</label>
                  <input data-testid="quote-ship-street" value={ship.street} onChange={setField("street")} placeholder="Straße & Hausnummer" className={inputCls} />
                  <div className="grid grid-cols-2 gap-3">
                    <input data-testid="quote-ship-zip" value={ship.zip} onChange={setField("zip")} placeholder="PLZ" className={inputCls} />
                    <input data-testid="quote-ship-city" value={ship.city} onChange={setField("city")} placeholder="Stadt" className={inputCls} />
                  </div>
                  <input data-testid="quote-ship-phone" value={ship.phone} onChange={setField("phone")} placeholder="Telefon (optional)" className={inputCls} />
                </div>

                <button onClick={accept} disabled={accepting || declining} data-testid="quote-accept-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 text-[#050505] py-4 font-medium hover:bg-emerald-400 transition-colors disabled:opacity-50">
                  <CreditCard size={18} /> {accepting ? "Weiterleitung zur Zahlung…" : `Angebot annehmen & bezahlen (${quote.quote_total.toFixed(2)} €)`}
                </button>
                <button onClick={decline} disabled={accepting || declining} data-testid="quote-decline-btn"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-[#aaa] py-3.5 text-sm hover:bg-white/5 hover:text-white transition-colors disabled:opacity-50">
                  <XCircle size={16} /> {declining ? "Wird abgelehnt…" : "Angebot ablehnen"}
                </button>
              </>
            )}
            <p className="text-xs text-[#666] mt-4 flex items-center gap-1.5"><FileText size={12} /> Dieses Angebot ist freibleibend und 14 Tage gültig.</p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
